import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import API_BASE_URL from '../../config/api';

interface User {
  id: string;
  email: string;
  name: string;
  isVerified: boolean;
}

interface SubscriptionService {
  id: string;
  serviceName: string;
  monthlyLimit: number | null;
  usedThisMonth: number;
  resetDate: string;
  isActive: boolean;
}

interface Subscription {
  id: string;
  planType: 'FREE' | 'BUSINESS' | 'ENTERPRISE';
  status: string;
  billingCycle: 'MONTHLY' | 'YEARLY';
  currentPeriodStart: string | null;
  currentPeriodEnd: string | null;
  cancelAtPeriodEnd: boolean;
  createdAt: string;
  user: User;
  subscriptionServices: SubscriptionService[];
  _count: {
    paymentHistory: number;
  };
}

interface PlanConfiguration {
  id: string;
  planType: 'FREE' | 'BUSINESS' | 'ENTERPRISE';
  name: string;
  description: string;
  features: string[];
  monthlyPrice: number;
  yearlyPrice: number;
  apiRequestsLimit: number | null;
  tokensLimit: number | null;
  isActive: boolean;
  popularBadge: boolean;
  savings: string;
  lastUpdatedBy: string;
  sortOrder: number;
}

const AdminSubscriptionManagement: React.FC = () => {
  const navigate = useNavigate();
  
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
  const [planConfigurations, setPlanConfigurations] = useState<PlanConfiguration[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedSubscription, setSelectedSubscription] = useState<Subscription | null>(null);
  const [showLimitsModal, setShowLimitsModal] = useState(false);
  const [showPlanConfigModal, setShowPlanConfigModal] = useState(false);
  const [showPlanUpdateModal, setShowPlanUpdateModal] = useState(false); // 🆕 NEW
  const [selectedPlanConfig, setSelectedPlanConfig] = useState<PlanConfiguration | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [filters, setFilters] = useState({
    search: '',
    planType: 'all',
    status: 'all',
    billingCycle: 'all'
  });

  // Form states
  const [limitsForm, setLimitsForm] = useState({
    apiRequestsLimit: '',
    tokensLimit: '',
    resetUsage: false
  });

  const [planConfigForm, setPlanConfigForm] = useState({
    name: '',
    description: '',
    features: [''],
    monthlyPrice: 0,
    yearlyPrice: 0,
    apiRequestsLimit: 0,
    tokensLimit: 0,
    isActive: true,
    popularBadge: false,
    savings: '',
    // stripeMonthlyPriceId: '',
    // stripeYearlyPriceId: ''
  });

  // 🆕 NEW: Plan update form state
  const [planUpdateForm, setPlanUpdateForm] = useState({
    planType: '',
    billingCycle: ''
  });

  useEffect(() => {
    fetchSubscriptions();
    fetchPlanConfigurations();
  }, [currentPage, filters]);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('adminToken');
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    };
  };

  const fetchSubscriptions = async () => {
    try {
      const queryParams = new URLSearchParams({
        page: currentPage.toString(),
        limit: '20',
        search: filters.search,
        planType: filters.planType,
        status: filters.status,
        billingCycle: filters.billingCycle
      });

      const response = await fetch(`${API_BASE_URL}/admin/subscriptions?${queryParams}`, {
        headers: getAuthHeaders()
      });

      if (response.ok) {
        const data = await response.json();
        setSubscriptions(data.data.subscriptions);
        setTotalPages(data.data.pagination.totalPages);
      }
    } catch (error) {
      console.error('Error fetching subscriptions:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPlanConfigurations = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/admin/plan-configurations`, {
        headers: getAuthHeaders()
      });

      if (response.ok) {
        const data = await response.json();
        setPlanConfigurations(data.data);
      }
    } catch (error) {
      console.error('Error fetching plan configurations:', error);
    }
  };

  const handleUpdateLimits = async () => {
    if (!selectedSubscription) return;

    try {
      const response = await fetch(`${API_BASE_URL}/admin/subscriptions/${selectedSubscription.id}/limits`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          apiRequestsLimit: limitsForm.apiRequestsLimit === '' ? undefined : 
                           limitsForm.apiRequestsLimit === 'unlimited' ? -1 : parseInt(limitsForm.apiRequestsLimit),
          tokensLimit: limitsForm.tokensLimit === '' ? undefined : 
                      limitsForm.tokensLimit === 'unlimited' ? -1 : parseInt(limitsForm.tokensLimit),
          resetUsage: limitsForm.resetUsage
        })
      });

      if (response.ok) {
        setShowLimitsModal(false);
        fetchSubscriptions();
        alert('Limits updated successfully');
      }
    } catch (error) {
      console.error('Error updating limits:', error);
      alert('Failed to update limits');
    }
  };

  // 🆕 NEW: Handle user plan update
  const handleUpdateUserPlan = async () => {
    if (!selectedSubscription) return;

    try {
      const response = await fetch(`${API_BASE_URL}/admin/subscriptions/${selectedSubscription.id}/plan`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          planType: planUpdateForm.planType,
          billingCycle: planUpdateForm.billingCycle
        })
      });

      if (response.ok) {
        setShowPlanUpdateModal(false);
        fetchSubscriptions();
        alert('User plan updated successfully');
      } else {
        const errorData = await response.json();
        alert(`Failed to update plan: ${errorData.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error updating user plan:', error);
      alert('Failed to update user plan');
    }
  };

  const handleResetUsage = async (subscriptionId: string) => {
    if (!window.confirm('Are you sure you want to reset usage counters?')) return;
    
    try {
      const response = await fetch(`${API_BASE_URL}/admin/subscriptions/${subscriptionId}/reset-usage`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({})
      });

      if (response.ok) {
        fetchSubscriptions();
        alert('Usage counters reset successfully');
      }
    } catch (error) {
      console.error('Error resetting usage:', error);
      alert('Failed to reset usage');
    }
  };

  const handleUpdatePlanConfig = async () => {
  if (!selectedPlanConfig) return;

  try {
    const response = await fetch(`${API_BASE_URL}/admin/plan-configurations/${selectedPlanConfig.planType}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify({
        name: planConfigForm.name,
        description: planConfigForm.description,
        features: planConfigForm.features.filter(f => f.trim() !== ''),
        monthlyPrice: planConfigForm.monthlyPrice,
        yearlyPrice: planConfigForm.yearlyPrice,
        // Convert 0 to null for unlimited
        apiRequestsLimit: planConfigForm.apiRequestsLimit === 0 ? null : planConfigForm.apiRequestsLimit,
        tokensLimit: planConfigForm.tokensLimit === 0 ? null : planConfigForm.tokensLimit,
        isActive: planConfigForm.isActive,
        popularBadge: planConfigForm.popularBadge,
        savings: planConfigForm.savings,
        // 🔒 CRITICAL: Don't send Stripe price IDs - let backend preserve them
        // This prevents overwriting existing Stripe price IDs with empty values
      })
    });

    if (response.ok) {
      setShowPlanConfigModal(false);
      fetchPlanConfigurations();
      alert('Plan configuration updated successfully');
    }
  } catch (error) {
    console.error('Error updating plan configuration:', error);
    alert('Failed to update plan configuration');
  }
};

  // const handleUpdatePlanConfig = async () => {
  //   if (!selectedPlanConfig) return;

  //   try {
  //     const response = await fetch(`${API_BASE_URL}/admin/plan-configurations/${selectedPlanConfig.planType}`, {
  //       method: 'PUT',
  //       headers: getAuthHeaders(),
  //       body: JSON.stringify({
  //         ...planConfigForm,
  //         features: planConfigForm.features.filter(f => f.trim() !== ''),
  //         // Convert 0 to null for unlimited
  //         apiRequestsLimit: planConfigForm.apiRequestsLimit === 0 ? null : planConfigForm.apiRequestsLimit,
  //         tokensLimit: planConfigForm.tokensLimit === 0 ? null : planConfigForm.tokensLimit
  //       })
  //     });

  //     if (response.ok) {
  //       setShowPlanConfigModal(false);
  //       fetchPlanConfigurations();
  //       alert('Plan configuration updated successfully');
  //     }
  //   } catch (error) {
  //     console.error('Error updating plan configuration:', error);
  //     alert('Failed to update plan configuration');
  //   }
  // };

  const handleInitializeDefaultPlans = async () => {
    if (!window.confirm('This will reset all plan configurations to defaults. Continue?')) return;

    try {
      const response = await fetch(`${API_BASE_URL}/admin/plan-configurations/initialize`, {
        method: 'POST',
        headers: getAuthHeaders()
      });

      if (response.ok) {
        fetchPlanConfigurations();
        alert('Default plan configurations initialized successfully');
      }
    } catch (error) {
      console.error('Error initializing default plans:', error);
      alert('Failed to initialize default plans');
    }
  };

  const handleTogglePlanStatus = async (planType: string) => {
    try {
      const response = await fetch(`${API_BASE_URL}/admin/plan-configurations/${planType}/toggle`, {
        method: 'PATCH',
        headers: getAuthHeaders()
      });

      if (response.ok) {
        fetchPlanConfigurations();
        alert('Plan status updated successfully');
      }
    } catch (error) {
      console.error('Error toggling plan status:', error);
      alert('Failed to update plan status');
    }
  };

  const openLimitsModal = (subscription: Subscription) => {
    setSelectedSubscription(subscription);
    const apiService = subscription.subscriptionServices.find(s => s.serviceName === 'API_REQUESTS');
    const tokenService = subscription.subscriptionServices.find(s => s.serviceName === 'TOKENS');
    
    setLimitsForm({
      apiRequestsLimit: apiService?.monthlyLimit === null ? 'unlimited' : apiService?.monthlyLimit?.toString() || '',
      tokensLimit: tokenService?.monthlyLimit === null ? 'unlimited' : tokenService?.monthlyLimit?.toString() || '',
      resetUsage: false
    });
    setShowLimitsModal(true);
  };

  // 🆕 NEW: Open plan update modal
  const openPlanUpdateModal = (subscription: Subscription) => {
    setSelectedSubscription(subscription);
    setPlanUpdateForm({
      planType: subscription.planType,
      billingCycle: subscription.billingCycle
    });
    setShowPlanUpdateModal(true);
  };

  const openPlanConfigModal = (planConfig: PlanConfiguration) => {
    setSelectedPlanConfig(planConfig);
    setPlanConfigForm({
      name: planConfig.name,
      description: planConfig.description,
      features: planConfig.features.length > 0 ? planConfig.features : [''],
      monthlyPrice: planConfig.monthlyPrice / 100, // Convert from cents to dollars
      yearlyPrice: planConfig.yearlyPrice / 100,
      apiRequestsLimit: planConfig.apiRequestsLimit || 0,
      tokensLimit: planConfig.tokensLimit || 0,
      isActive: planConfig.isActive,
      popularBadge: planConfig.popularBadge,
      savings: planConfig.savings || '',
      // stripeMonthlyPriceId: '',
      // stripeYearlyPriceId: ''
    });
    setShowPlanConfigModal(true);
  };

  const addFeature = () => {
    setPlanConfigForm(prev => ({
      ...prev,
      features: [...prev.features, '']
    }));
  };

  const removeFeature = (index: number) => {
    setPlanConfigForm(prev => ({
      ...prev,
      features: prev.features.filter((_, i) => i !== index)
    }));
  };

  const updateFeature = (index: number, value: string) => {
    setPlanConfigForm(prev => ({
      ...prev,
      features: prev.features.map((feature, i) => i === index ? value : feature)
    }));
  };

  const getPlanColor = (planType: string) => {
    switch (planType) {
      case 'FREE': return 'bg-gray-100 text-gray-800';
      case 'BUSINESS': return 'bg-blue-100 text-blue-800';
      case 'ENTERPRISE': return 'bg-purple-100 text-purple-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'ACTIVE': return 'bg-green-100 text-green-800';
      case 'CANCELED': return 'bg-red-100 text-red-800';
      case 'PAST_DUE': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const formatNumber = (num: number | null) => {
    if (num === null) return 'Unlimited';
    return num.toLocaleString();
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount / 100);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading subscriptions...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Subscription Management</h1>
              <p className="text-gray-600">Manage plan configurations and user subscription limits</p>
            </div>
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/admin/dashboard')}
                className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-md text-sm font-medium"
              >
                Back to Dashboard
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
        
        {/* Plan Configuration Management Section */}
        <div className="bg-white shadow rounded-lg mb-8">
          <div className="px-4 py-5 sm:p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg leading-6 font-medium text-gray-900">
                Website Plan Configurations
              </h3>
              <button
                onClick={handleInitializeDefaultPlans}
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md text-sm font-medium"
              >
                Initialize Defaults
              </button>
            </div>
            <p className="text-sm text-gray-600 mb-6">
              Edit how plans are displayed on your website. Changes here affect pricing and features shown to users.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {planConfigurations.map((plan) => (
                <div key={plan.id} className="border rounded-lg p-4 hover:shadow-md transition-shadow">
                  <div className="flex justify-between items-start mb-2">
                    <h4 className="text-lg font-semibold text-gray-900">{plan.name}</h4>
                    <div className="flex items-center space-x-2">
                      <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getPlanColor(plan.planType)}`}>
                        {plan.planType}
                      </span>
                      {plan.popularBadge && (
                        <span className="px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                          Popular
                        </span>
                      )}
                    </div>
                  </div>
                  <p className="text-sm text-gray-600 mb-3">{plan.description}</p>
                  <div className="space-y-2 text-sm mb-4">
                    <div>
                      <span className="font-medium">Monthly:</span> {formatCurrency(plan.monthlyPrice)}
                    </div>
                    <div>
                      <span className="font-medium">Yearly:</span> {formatCurrency(plan.yearlyPrice)}
                    </div>
                    {plan.savings && (
                      <div className="text-green-600 text-xs">{plan.savings}</div>
                    )}
                    <div>
                      <span className="font-medium">API Requests:</span> {formatNumber(plan.apiRequestsLimit)}
                    </div>
                    <div>
                      <span className="font-medium">Tokens:</span> {formatNumber(plan.tokensLimit)}
                    </div>
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className={`text-xs px-2 py-1 rounded-full ${plan.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                        {plan.isActive ? 'Active' : 'Inactive'}
                      </span>
                      <button
                        onClick={() => handleTogglePlanStatus(plan.planType)}
                        className="text-xs text-blue-600 hover:text-blue-900"
                      >
                        {plan.isActive ? 'Deactivate' : 'Activate'}
                      </button>
                    </div>
                    <button
                      onClick={() => openPlanConfigModal(plan)}
                      className="w-full text-blue-600 hover:text-blue-900 text-sm font-medium border border-blue-200 rounded py-1"
                    >
                      Edit Configuration
                    </button>
                  </div>
                  {plan.lastUpdatedBy && (
                    <div className="mt-2 text-xs text-gray-500">
                      Last updated by: {plan.lastUpdatedBy}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white shadow rounded-lg mb-6">
          <div className="p-6">
            <h4 className="text-md font-medium text-gray-900 mb-4">Filter User Subscriptions</h4>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Search</label>
                <input
                  type="text"
                  placeholder="Search by email or name..."
                  value={filters.search}
                  onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Plan Type</label>
                <select
                  value={filters.planType}
                  onChange={(e) => setFilters({ ...filters, planType: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="all">All Plans</option>
                  <option value="FREE">Free</option>
                  <option value="BUSINESS">Business</option>
                  <option value="ENTERPRISE">Enterprise</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                <select
                  value={filters.status}
                  onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="all">All Statuses</option>
                  <option value="ACTIVE">Active</option>
                  <option value="CANCELED">Canceled</option>
                  <option value="PAST_DUE">Past Due</option>
                  <option value="TRIALING">Trialing</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Billing Cycle</label>
                <select
                  value={filters.billingCycle}
                  onChange={(e) => setFilters({ ...filters, billingCycle: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="all">All Cycles</option>
                  <option value="MONTHLY">Monthly</option>
                  <option value="YEARLY">Yearly</option>
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* User Subscriptions Table */}
        <div className="bg-white shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
              User Subscriptions ({subscriptions.length})
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              Manage individual user subscription limits and usage. You can now change user plans directly.
            </p>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Plan</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Usage</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {subscriptions.map((subscription) => {
                    const apiService = subscription.subscriptionServices.find(s => s.serviceName === 'API_REQUESTS');
                    const tokenService = subscription.subscriptionServices.find(s => s.serviceName === 'TOKENS');
                    
                    return (
                      <tr key={subscription.id}>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div>
                              <div className="text-sm font-medium text-gray-900">{subscription.user.name}</div>
                              <div className="text-sm text-gray-500">{subscription.user.email}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getPlanColor(subscription.planType)}`}>
                            {subscription.planType}
                          </span>
                          <div className="text-xs text-gray-500 mt-1">{subscription.billingCycle}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(subscription.status)}`}>
                            {subscription.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          <div className="space-y-1">
                            <div>API: {apiService?.usedThisMonth || 0} / {formatNumber(apiService?.monthlyLimit || 0)}</div>
                            <div>Tokens: {tokenService?.usedThisMonth || 0} / {formatNumber(tokenService?.monthlyLimit || 0)}</div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                          {/* 🆕 NEW: Change Plan button */}
                          {/* <button
                            onClick={() => openPlanUpdateModal(subscription)}
                            className="text-blue-600 hover:text-blue-900"
                          >
                            Change Plan
                          </button> */}
                          <button
                            onClick={() => openLimitsModal(subscription)}
                            className="text-green-600 hover:text-green-900"
                          >
                            Edit Limits
                          </button>
                          <button
                            onClick={() => handleResetUsage(subscription.id)}
                            className="text-yellow-600 hover:text-yellow-900"
                          >
                            Reset Usage
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="flex items-center justify-between mt-6">
              <div className="flex-1 flex justify-between sm:hidden">
                <button
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                >
                  Previous
                </button>
                <button
                  onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage === totalPages}
                  className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                >
                  Next
                </button>
              </div>
              <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm text-gray-700">
                    Page <span className="font-medium">{currentPage}</span> of{' '}
                    <span className="font-medium">{totalPages}</span>
                  </p>
                </div>
                <div>
                  <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                    <button
                      onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                      disabled={currentPage === 1}
                      className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                    >
                      Previous
                    </button>
                    <button
                      onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                      disabled={currentPage === totalPages}
                      className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                    >
                      Next
                    </button>
                  </nav>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 🆕 NEW: Plan Update Modal */}
      {showPlanUpdateModal && selectedSubscription && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <h3 className="text-lg font-bold text-gray-900 mb-4">
              Update Plan - {selectedSubscription.user.name}
            </h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Current Plan: <span className="font-bold text-blue-600">{selectedSubscription.planType}</span>
                </label>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">New Plan Type</label>
                <select
                  value={planUpdateForm.planType}
                  onChange={(e) => setPlanUpdateForm({ ...planUpdateForm, planType: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select Plan Type</option>
                  <option value="FREE">Free</option>
                  <option value="BUSINESS">Business</option>
                  <option value="ENTERPRISE">Enterprise</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Billing Cycle</label>
                <select
                  value={planUpdateForm.billingCycle}
                  onChange={(e) => setPlanUpdateForm({ ...planUpdateForm, billingCycle: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  disabled={planUpdateForm.planType === 'FREE'}
                >
                  <option value="">Select Billing Cycle</option>
                  <option value="MONTHLY">Monthly</option>
                  <option value="YEARLY">Yearly</option>
                </select>
              </div>
              {planUpdateForm.planType === 'FREE' && (
                <div className="bg-yellow-50 border border-yellow-200 rounded p-3">
                  <p className="text-sm text-yellow-800">
                    Free plan doesn't require billing cycle selection.
                  </p>
                </div>
              )}
              {planUpdateForm.planType && planUpdateForm.planType !== 'FREE' && !planUpdateForm.billingCycle && (
                <div className="bg-red-50 border border-red-200 rounded p-3">
                  <p className="text-sm text-red-800">
                    Please select a billing cycle for paid plans.
                  </p>
                </div>
              )}
            </div>
            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => setShowPlanUpdateModal(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300"
              >
                Cancel
              </button>
              <button
                onClick={handleUpdateUserPlan}
                disabled={!planUpdateForm.planType || (planUpdateForm.planType !== 'FREE' && !planUpdateForm.billingCycle)}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
              >
                Update Plan
              </button>
            </div>
          </div>
        </div>
      )}

      {/* User Limits Modal */}
      {showLimitsModal && selectedSubscription && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <h3 className="text-lg font-bold text-gray-900 mb-4">
              Update Limits - {selectedSubscription.user.name}
            </h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">API Requests Limit</label>
                <input
                  type="text"
                  placeholder="Enter number or 'unlimited'"
                  value={limitsForm.apiRequestsLimit}
                  onChange={(e) => setLimitsForm({ ...limitsForm, apiRequestsLimit: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Tokens Limit</label>
                <input
                  type="text"
                  placeholder="Enter number or 'unlimited'"
                  value={limitsForm.tokensLimit}
                  onChange={(e) => setLimitsForm({ ...limitsForm, tokensLimit: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="resetUsage"
                  checked={limitsForm.resetUsage}
                  onChange={(e) => setLimitsForm({ ...limitsForm, resetUsage: e.target.checked })}
                  className="mr-2"
                />
                <label htmlFor="resetUsage" className="text-sm text-gray-700">
                  Reset current usage to 0
                </label>
              </div>
            </div>
            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => setShowLimitsModal(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300"
              >
                Cancel
              </button>
              <button
                onClick={handleUpdateLimits}
                className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700"
              >
                Update Limits
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Plan Configuration Modal */}
      {showPlanConfigModal && selectedPlanConfig && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-6 border w-full max-w-2xl shadow-lg rounded-md bg-white">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-bold text-gray-900">
                Edit {selectedPlanConfig.planType} Plan Configuration
              </h3>
              <button
                onClick={() => setShowPlanConfigModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                </svg>
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Plan Name</label>
                <input
                  type="text"
                  value={planConfigForm.name}
                  onChange={(e) => setPlanConfigForm({ ...planConfigForm, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  value={planConfigForm.description}
                  onChange={(e) => setPlanConfigForm({ ...planConfigForm, description: e.target.value })}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Monthly Price ($)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={planConfigForm.monthlyPrice}
                    onChange={(e) => setPlanConfigForm({ ...planConfigForm, monthlyPrice: parseFloat(e.target.value) || 0 })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Yearly Price ($)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={planConfigForm.yearlyPrice}
                    onChange={(e) => setPlanConfigForm({ ...planConfigForm, yearlyPrice: parseFloat(e.target.value) || 0 })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">API Requests Limit (0 = unlimited)</label>
                  <input
                    type="number"
                    value={planConfigForm.apiRequestsLimit}
                    onChange={(e) => setPlanConfigForm({ ...planConfigForm, apiRequestsLimit: parseInt(e.target.value) || 0 })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Tokens Limit (0 = unlimited)</label>
                  <input
                    type="number"
                    value={planConfigForm.tokensLimit}
                    onChange={(e) => setPlanConfigForm({ ...planConfigForm, tokensLimit: parseInt(e.target.value) || 0 })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Savings Text</label>
                <input
                  type="text"
                  placeholder="e.g., Save 20% yearly"
                  value={planConfigForm.savings}
                  onChange={(e) => setPlanConfigForm({ ...planConfigForm, savings: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Features</label>
                {planConfigForm.features.map((feature, index) => (
                  <div key={index} className="flex items-center space-x-2 mb-2">
                    <input
                      type="text"
                      placeholder="Feature description"
                      value={feature}
                      onChange={(e) => updateFeature(index, e.target.value)}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <button
                      onClick={() => removeFeature(index)}
                      className="text-red-600 hover:text-red-800"
                    >
                      Remove
                    </button>
                  </div>
                ))}
                <button
                  onClick={addFeature}
                  className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                >
                  + Add Feature
                </button>
              </div>

              <div className="flex items-center space-x-4">
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="isActive"
                    checked={planConfigForm.isActive}
                    onChange={(e) => setPlanConfigForm({ ...planConfigForm, isActive: e.target.checked })}
                    className="mr-2"
                  />
                  <label htmlFor="isActive" className="text-sm text-gray-700">
                    Active Plan
                  </label>
                </div>
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="popularBadge"
                    checked={planConfigForm.popularBadge}
                    onChange={(e) => setPlanConfigForm({ ...planConfigForm, popularBadge: e.target.checked })}
                    className="mr-2"
                  />
                  <label htmlFor="popularBadge" className="text-sm text-gray-700">
                    Popular Badge
                  </label>
                </div>
              </div>

              {selectedPlanConfig.planType !== 'FREE' && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <h4 className="text-sm font-medium text-yellow-800 mb-2">Stripe Configuration</h4>
                  <p className="text-xs text-yellow-700 mb-3">
                    Make sure your Stripe price IDs are configured in your environment variables:
                  </p>
                  <div className="space-y-2 text-xs text-yellow-700">
                    <div>STRIPE_{selectedPlanConfig.planType}_MONTHLY_PRICE_ID</div>
                    <div>STRIPE_{selectedPlanConfig.planType}_YEARLY_PRICE_ID</div>
                  </div>
                </div>
              )}
            </div>

            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => setShowPlanConfigModal(false)}
                className="px-6 py-2 text-sm font-medium text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300"
              >
                Cancel
              </button>
              <button
                onClick={handleUpdatePlanConfig}
                className="px-6 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
              >
                Update Plan Configuration
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminSubscriptionManagement;